import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import type { Tag } from '@/models/media-network/Advertiser';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import { useToast } from '../common/toast/ToastProvider';

// Tags are brand-wide and deliberately NOT owned by Ad Banner: Cross-Sell will tag its own content from this
// same list, so a tag means the same thing everywhere it's used. That's why this is its own nav item rather
// than a panel inside the banner screens.
const Tags = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.mediaNetworkTags);
    const canDelete = perms.canDelete(Perm.mediaNetworkTags);

    const brandId = app.brandId ?? undefined;

    const [newTag, setNewTag] = useState('');

    const { data: tags, isPending } = useQuery({
        queryKey: mediaNetworkKeys.tags(brandId ?? ''),
        enabled: !!brandId,
        queryFn: () => mediaNetworkApi.listTags(brandId!),
    });

    const { mutateAsync: addTag, isPending: isAddingTag } = useMutation({
        mutationFn: (name: string) => mediaNetworkApi.createTag({ brandId: brandId!, name }),
        onSuccess: async () => {
            setNewTag('');
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.tags(brandId ?? '') });
        },
        onError: () => toast({ severity: 'error', message: strings.playlistTagSaveError }),
    });

    const { mutateAsync: removeTag } = useMutation({
        mutationFn: (id: string) => mediaNetworkApi.deleteTag(id, brandId!),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.playlistTagDeleted });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.tags(brandId ?? '') });
            // Media carries tag ids, so the playlist list is stale once a tag is gone.
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.list(brandId ?? '') });
        },
        onError: () => toast({ severity: 'error', message: strings.playlistTagDeleteError }),
    });

    const columns: GridColDef<Tag>[] = [
        { field: 'name', headerName: strings.playlistTagName, flex: 2 },
        { field: 'mediaCount', headerName: strings.playlistMediaCount, width: 90 },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', width: 80,
            getActions: (params: GridRowParams<Tag>) => [
                <DataGridDeleteButton
                    id={params.id.toString()}
                    getWarningMessage={(id) => strings.playlistTagDeleteConfirm
                        .replace('{{name}}', tags?.find(t => t.id === id)?.name ?? '')}
                    onClick={(id) => removeTag(id)}
                    aria-label="delete tag"
                    hidden={!canDelete}
                />,
            ],
        },
    ];

    if (!brandId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Typography variant="body2" color="text.secondary">{strings.playlistSelectBrandTopBar}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
            <Stack spacing={1} sx={{ height: '100%', minHeight: 0, maxWidth: 700 }}>
                <Typography variant="h6">{strings.playlistTagsTitle}</Typography>

                {/* Added inline rather than through a dialog: a tag is one short string, and tagging is a
                    pick-or-type interaction. Creating one that already exists returns the existing tag. */}
                {canCreate && (
                    <Stack direction="row" spacing={1}>
                        <TextField
                            label={strings.playlistNewTag}
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && newTag.trim()) {
                                    e.preventDefault();
                                    void addTag(newTag.trim());
                                }
                            }}
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            onClick={() => void addTag(newTag.trim())}
                            loading={isAddingTag}
                            disabled={!newTag.trim() || isAddingTag}
                            size="input"
                        >
                            {strings.add}
                        </Button>
                    </Stack>
                )}

                <Box sx={{ flex: 1, minHeight: 300 }}>
                    <DataGrid
                        rows={tags ?? []}
                        columns={columns}
                        loading={isPending}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        localeText={{ noRowsLabel: strings.playlistNoTags }}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export default Tags;
